import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ArrowLeft, BookOpen, Target, Clock, PlayCircle, RotateCcw } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function CaseStudies() {
  const navigate = useNavigate();
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchCases();
  }, []);

  const fetchCases = async () => {
    try {
      const res = await fetch(`${API}/api/case-studies`, { credentials: 'include' });
      if (!res.ok) throw new Error('Failed to fetch case studies');
      const data = await res.json();
      setCases(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-pulse text-slate-500">Loading case studies...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <Button
            variant="ghost"
            onClick={() => navigate('/dashboard')}
            className="text-slate-600 hover:text-slate-900 mb-4"
            data-testid="back-to-dashboard"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
          <div className="flex items-center gap-3">
            <div className="p-3 bg-purple-100 rounded-lg">
              <BookOpen className="w-8 h-8 text-purple-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Case Studies</h1>
              <p className="text-slate-500">Practice clinical decision-making with realistic scenarios</p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-6xl mx-auto px-4 py-8">
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 text-red-700">
            {error}
          </div>
        )}

        {cases.length === 0 ? (
          <Card className="bg-white border-slate-200">
            <CardContent className="py-12 text-center">
              <BookOpen className="w-12 h-12 text-slate-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-700 mb-2">No Case Studies Available</h3>
              <p className="text-slate-500">Check back later for new clinical cases.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {cases.map((caseStudy) => (
              <Card
                key={caseStudy.case_id}
                className="bg-white border-slate-200 hover:border-purple-300 hover:shadow-md transition-all cursor-pointer"
                onClick={() => navigate(`/case-studies/${caseStudy.case_id}`)}
                data-testid={`case-card-${caseStudy.case_id}`}
              >
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <CardTitle className="text-lg text-slate-900">{caseStudy.title}</CardTitle>
                    {caseStudy.has_in_progress_attempt && (
                      <Badge className="bg-amber-100 text-amber-700 border-amber-200">
                        In Progress
                      </Badge>
                    )}
                  </div>
                  <CardDescription className="text-slate-500 line-clamp-2">
                    {caseStudy.overview}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {/* Learning Objectives Preview */}
                    <div className="flex items-start gap-2 text-sm text-slate-600">
                      <Target className="w-4 h-4 mt-0.5 text-purple-500 flex-shrink-0" />
                      <span>{caseStudy.learning_objectives.length} learning objectives</span>
                    </div>
                    
                    {/* Stage Count */}
                    <div className="flex items-start gap-2 text-sm text-slate-600">
                      <Clock className="w-4 h-4 mt-0.5 text-blue-500 flex-shrink-0" />
                      <span>{caseStudy.stage_count} stages</span>
                    </div>

                    {/* Topics */}
                    {caseStudy.topic_keys?.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 pt-2">
                        {caseStudy.topic_keys.map((topic) => (
                          <Badge
                            key={topic}
                            variant="outline"
                            className="bg-slate-50 text-slate-600 border-slate-200 text-xs"
                          >
                            {topic}
                          </Badge>
                        ))}
                      </div>
                    )}

                    {/* Action Button */}
                    <Button
                      className={`w-full mt-4 ${
                        caseStudy.has_in_progress_attempt
                          ? 'bg-amber-500 hover:bg-amber-600 text-white'
                          : 'bg-purple-600 hover:bg-purple-700 text-white'
                      }`}
                      data-testid={`start-case-${caseStudy.case_id}`}
                    >
                      {caseStudy.has_in_progress_attempt ? (
                        <>
                          <RotateCcw className="w-4 h-4 mr-2" />
                          Resume Case
                        </>
                      ) : (
                        <>
                          <PlayCircle className="w-4 h-4 mr-2" />
                          Start Case
                        </>
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
